import { useEffect, useMemo, useState } from 'react';
import { usePortalQuery, EHttpMethod } from '../../hooks/usePortalQuery';
import { Contract, Appointment, MeterPointDetails, Invoice, CustomerGroup } from '../../types';
import { useFormContext } from 'react-hook-form';
import axios from 'axios';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { EnvironmentVariables } from '../../EnvironmentVariables';

interface TransferDetailsProps {
  handleSetInvoiceList: (invoices: { title: string; value: string }[]) => void;
  handleSetRenderForm: (render: boolean) => void;
}

export const TransferDetails = ({ handleSetInvoiceList, handleSetRenderForm }: TransferDetailsProps) => {
  const [transferSiteError, setTransferSiteError] = useState<string | null>(null);
  const { watch, setValue } = useFormContext<MeterPointDetails>();

  const [mprn, contractId, customerGroupId, transferCustomerGroupId, transferContractId, transferDate] = watch([
    'mprn',
    'contractId',
    'customerGroupId',
    'transferCustomerGroupId',
    'transferContractId',
    'transferDate'
  ]);

  const { data: customerGroups, isLoading: customerGroupsLoading } = usePortalQuery<CustomerGroup[]>(
    EHttpMethod.GET,
    'customer-groups',
    'customer-groups',
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    600000,
    600000
  );

  const { data: customerContracts, isLoading: customerContractsLoading } = usePortalQuery<Contract[]>(
    EHttpMethod.GET,
    `customer-contracts/${transferCustomerGroupId}?filter=Y`,
    `customer-contracts/${transferCustomerGroupId}?filter=Y`,
    !!transferCustomerGroupId
  );

  const transferCustomerGroupOptions = useMemo(() => {
    if (customerGroupsLoading || !customerGroups) return [];
    return (
      customerGroups.map(group => ({
        title: group.customerGroupName,
        value: group.customerGroupId
      })) ?? []
    );
  }, [customerGroupId, customerGroups, customerGroupsLoading]);

  const transferContractIdOptions = useMemo(() => {
    if (customerContractsLoading || !customerContracts) return [];
    return customerContracts
      .filter(contract => contract.contractId != contractId)
      .map(({ contractId, contractType }) => ({
        title: `${contractType} (Contract ID: ${contractId})`,
        value: contractId
      }));
  }, [contractId, customerContracts, customerContractsLoading]);

  useEffect(() => {
    setValue('transferContractId', undefined);
    setValue('transferDate', undefined);
    setTransferSiteError(null);
    handleSetRenderForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferCustomerGroupId]);

  useEffect(() => {
    if (!customerContracts || !transferContractId) return undefined;
    const selectedContract = customerContracts.find(c => c.contractId === transferContractId);
    if (!selectedContract) return undefined;
    setValue('transferContractStartDate', selectedContract.contractStartDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferContractId]);

  const getTransferInvoices = async () => {
    const response = await axios
      .get(`${EnvironmentVariables.ApiUrl}/invoices/${transferCustomerGroupId}`, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      })
      .catch(error => error.response);
    if (!response) {
      setTransferSiteError('An error occurred fetching transfer sites invoice details.');
      return;
    }
    if (response.status !== 200) {
      setTransferSiteError(response.data);
      return;
    }

    if (transferCustomerGroupId && transferContractId) {
      const newInvoices: Invoice[] = response.data;
      handleSetInvoiceList(
        newInvoices.map((invoice: Invoice) => ({
          title: `${invoice.customerInvoiceName} ${invoice.defaultInvoiceGroup ? '(Default)' : ''} ${invoice.contractId ? `(Contract ID: ${invoice.contractId})` : ''}`,
          value: invoice.invoiceGroupId
        }))
      );
      const defaultInvoice = newInvoices.find(invoice => invoice.defaultInvoiceGroup);
      if (defaultInvoice) {
        setValue('invoiceGroupId', defaultInvoice.invoiceGroupId);
        return;
      }
      const invoiceForContract = newInvoices.find(invoice => invoice.contractId === transferContractId);
      if (invoiceForContract) {
        setValue('invoiceGroupId', invoiceForContract.invoiceGroupId);
      }
    }
  };

  const onGetTransferApptDataSuccess = (transferApptData: Appointment) => {
    if (!transferApptData) return;

    setTransferSiteError(null);

    if (transferApptData.duplicate && !transferApptData.hasOpenExceptions) {
      setTransferSiteError('This site is already assigned to the currently selected Transfer Customer/Contract ID. Please select a different Transfer Customer/Contract ID to proceed.');
      return;
    } else if (transferApptData.hasOpenExceptions && !transferApptData.duplicate) {
      setTransferSiteError('There are open exceptions related to this MPAN. Please resolve these before re-attempting site transfer to a different contract.');
      return;
    } else if (transferApptData.hasOpenExceptions && transferApptData.duplicate) {
      setTransferSiteError(
        'This site is already assigned to the currently selected Transfer Customer/Contract ID and there are open exceptions related to this MPAN. Please resolve these before proceeding.'
      );
      return;
    }

    // Appt data is good, get the relevant invoice details
    getTransferInvoices();

    if (!transferApptData.active) {
      setValue('contractSiteStatusId', '5'); // Inactive
    } else {
      setValue('contractSiteStatusId', '10'); // Active
    }
    // will allow the rest of the form the render in <MeterPointDetailsForm />
    handleSetRenderForm(true);
  };

  const { data: transferApptData } = usePortalQuery<Appointment>(
    EHttpMethod.GET,
    `get-appointment/${mprn}/${transferContractId}`,
    `get-appointment/${mprn}-${transferContractId}`,
    !!transferCustomerGroupId && !!transferContractId && !!mprn && !!transferDate,
    undefined,
    onGetTransferApptDataSuccess
  );

  return (
    <>
      {transferApptData && !transferApptData?.active && <p className="text-center py-4">No Valid MOP Appointment for MPAN: {mprn}.</p>}
      <div className="pb-4">
        <FormSearchDropdown title="Transfer Customer Group" name="transferCustomerGroupId" placeholder="Select Transfer Customer Group" elementsForDisplay={transferCustomerGroupOptions} mandatory />
        {transferCustomerGroupId && (
          <FormDropdown
            title="Transfer Contract"
            name="transferContractId"
            placeholder="Select Transfer Contract"
            elementsForDisplay={transferContractIdOptions}
            mandatory
            readonly={customerContractsLoading}
          />
        )}
        {!customerContractsLoading && transferCustomerGroupId && customerContracts && customerContracts.length === 0 && (
          <div className="pb-4 text-feedback-red inline-block">{'This customer group has no active contracts.'}</div>
        )}
        {transferCustomerGroupId && transferContractId && <FormDatePicker name="transferDate" title="Transfer Date" mandatory />}
      </div>
      {transferSiteError && (
        <p className="pb-5 text-center text-lg text-feedback-red">
          {transferSiteError}
          <br />
        </p>
      )}
      {(!transferContractId || !transferDate) && <p className="pb-5 text-center text-lg text-feedback-orange">Please select a Transfer Contract and date to continue.</p>}
    </>
  );
};
